import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, MessageSquare, CheckCircle, Globe, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useUpdateAssistant, useDeleteAssistant, AssistantDetail } from '@/hooks/useAssistants'

interface SettingsTabProps {
  assistantId: string
  assistant: AssistantDetail
}

interface LLMSettings {
  temperature: number
  top_p: number
  presence_penalty: number
  frequency_penalty: number
}

interface RetrievalSettings {
  similarity_threshold: number
  keywords_similarity_weight: number
  top_n: number
  show_quote: boolean
}

interface CrawlerSettings {
  enable_crawler: boolean
  crawler_include_domains: string
  crawler_exclude_domains: string
}

const DEFAULT_LLM_SETTINGS: LLMSettings = {
  temperature: 0.3,
  top_p: 0.3,
  presence_penalty: 0.4,
  frequency_penalty: 0.7,
}

const DEFAULT_RETRIEVAL_SETTINGS: RetrievalSettings = {
  similarity_threshold: 0.2,
  keywords_similarity_weight: 0.5,
  top_n: 3,
  show_quote: true,
}

const DEFAULT_CRAWLER_SETTINGS: CrawlerSettings = {
  enable_crawler: false,
  crawler_include_domains: '',
  crawler_exclude_domains: '',
}

export function SettingsTab({ assistantId, assistant }: SettingsTabProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const updateAssistant = useUpdateAssistant()
  const deleteAssistant = useDeleteAssistant()

  const [llmSettings, setLlmSettings] = useState<LLMSettings>({
    temperature: assistant.temperature,
    top_p: assistant.top_p,
    presence_penalty: assistant.presence_penalty,
    frequency_penalty: assistant.frequency_penalty,
  })

  const [retrievalSettings, setRetrievalSettings] = useState<RetrievalSettings>({
    similarity_threshold: assistant.similarity_threshold,
    keywords_similarity_weight: assistant.keywords_similarity_weight,
    top_n: assistant.top_n,
    show_quote: assistant.show_quote,
  })

  const [crawlerSettings, setCrawlerSettings] = useState<CrawlerSettings>({
    enable_crawler: assistant.enable_crawler ?? false,
    crawler_include_domains: assistant.crawler_include_domains ?? '',
    crawler_exclude_domains: assistant.crawler_exclude_domains ?? '',
  })

  const [hasChanges, setHasChanges] = useState(false)

  // Track changes
  useEffect(() => {
    const llmChanged =
      llmSettings.temperature !== assistant.temperature ||
      llmSettings.top_p !== assistant.top_p ||
      llmSettings.presence_penalty !== assistant.presence_penalty ||
      llmSettings.frequency_penalty !== assistant.frequency_penalty
    
    const retrievalChanged =
      retrievalSettings.similarity_threshold !== assistant.similarity_threshold ||
      retrievalSettings.keywords_similarity_weight !== assistant.keywords_similarity_weight ||
      retrievalSettings.top_n !== assistant.top_n ||
      retrievalSettings.show_quote !== assistant.show_quote
    
    const crawlerChanged =
      crawlerSettings.enable_crawler !== (assistant.enable_crawler ?? false) ||
      crawlerSettings.crawler_include_domains !== (assistant.crawler_include_domains ?? '') ||
      crawlerSettings.crawler_exclude_domains !== (assistant.crawler_exclude_domains ?? '')
    
    setHasChanges(llmChanged || retrievalChanged || crawlerChanged)
  }, [llmSettings, retrievalSettings, crawlerSettings, assistant])

  const handleSave = async () => {
    try {
      await updateAssistant.mutateAsync({
        assistantId,
        data: {
          ...llmSettings,
          ...retrievalSettings,
          ...crawlerSettings,
        },
      })
      toast({
        title: 'Settings saved',
        description: 'Settings have been saved successfully.',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save settings',
        variant: 'destructive',
      })
    }
  }

  const handleResetLLM = () => {
    setLlmSettings(DEFAULT_LLM_SETTINGS)
  }

  const handleResetRetrieval = () => {
    setRetrievalSettings(DEFAULT_RETRIEVAL_SETTINGS)
  }

  const handleResetCrawler = () => {
    setCrawlerSettings(DEFAULT_CRAWLER_SETTINGS)
  }

  const handleDelete = async () => {
    try {
      await deleteAssistant.mutateAsync(assistantId)
      toast({
        title: 'Assistant deleted',
        description: `"${assistant.name}" has been deleted.`,
      })
      navigate('/app/assistants')
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete assistant',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* LLM Settings Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>LLM Configuration</CardTitle>
            <CardDescription>Configure how the AI model generates responses</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Model (Disabled) */}
          <div className="space-y-2">
            <Label>Model</Label>
            <Input value="Managed by ChatterPillar" disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">
              Model selection is configured at the organization level
            </p>
          </div>

          {/* Temperature */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Temperature</Label>
              <span className="text-sm font-medium">{llmSettings.temperature.toFixed(1)}</span>
            </div>
            <Slider
              value={[llmSettings.temperature]}
              onValueChange={([value]) => setLlmSettings({ ...llmSettings, temperature: value })}
              min={0}
              max={1}
              step={0.1}
            />
            <p className="text-xs text-muted-foreground">
              Controls randomness. Lower = more focused, Higher = more creative
            </p>
          </div>

          {/* Top P */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Top P</Label>
              <span className="text-sm font-medium">{llmSettings.top_p.toFixed(1)}</span>
            </div>
            <Slider
              value={[llmSettings.top_p]}
              onValueChange={([value]) => setLlmSettings({ ...llmSettings, top_p: value })}
              min={0}
              max={1}
              step={0.1}
            />
            <p className="text-xs text-muted-foreground">
              Nucleus sampling threshold. Lower = more conservative
            </p>
          </div>

          {/* Presence Penalty */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Presence Penalty</Label>
              <span className="text-sm font-medium">{llmSettings.presence_penalty.toFixed(1)}</span>
            </div>
            <Slider
              value={[llmSettings.presence_penalty]}
              onValueChange={([value]) => setLlmSettings({ ...llmSettings, presence_penalty: value })}
              min={0}
              max={2}
              step={0.1}
            />
            <p className="text-xs text-muted-foreground">
              Reduces repetition of topics already discussed
            </p>
          </div>

          {/* Frequency Penalty */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Frequency Penalty</Label>
              <span className="text-sm font-medium">{llmSettings.frequency_penalty.toFixed(1)}</span>
            </div>
            <Slider
              value={[llmSettings.frequency_penalty]}
              onValueChange={([value]) => setLlmSettings({ ...llmSettings, frequency_penalty: value })}
              min={0}
              max={2}
              step={0.1}
            />
            <p className="text-xs text-muted-foreground">
              Reduces repetition of specific words/phrases
            </p>
          </div>

          {/* Bottom buttons */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={handleResetLLM}>
              Reset to Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Retrieval Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Retrieval & Chat Settings
          </CardTitle>
          <CardDescription>Configure how documents are retrieved and responses are generated</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-900 dark:text-green-100 text-sm">
                These settings take effect immediately without requiring document reparsing.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Similarity Threshold */}
              <div className="space-y-2">
                <Label htmlFor="similarity_threshold">Similarity Threshold</Label>
                <Input
                  id="similarity_threshold"
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  value={retrievalSettings.similarity_threshold}
                  onChange={(e) => setRetrievalSettings({ ...retrievalSettings, similarity_threshold: parseFloat(e.target.value) || 0.2 })}
                />
                <p className="text-xs text-muted-foreground">
                  Minimum similarity score (0-1)
                </p>
              </div>

              {/* Keyword Weight */}
              <div className="space-y-2">
                <Label htmlFor="keywords_similarity_weight">Keyword Search Weight</Label>
                <Input
                  id="keywords_similarity_weight"
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  value={retrievalSettings.keywords_similarity_weight}
                  onChange={(e) => setRetrievalSettings({ ...retrievalSettings, keywords_similarity_weight: parseFloat(e.target.value) || 0.5 })}
                />
                <p className="text-xs text-muted-foreground">
                  Balance between semantic and keyword search (0-1)
                </p>
              </div>
            </div>

            {/* Context Chunks */}
            <div className="space-y-2">
              <Label htmlFor="top_n">Context Chunks to Retrieve</Label>
              <Input
                id="top_n"
                type="number"
                min={1}
                max={10}
                value={retrievalSettings.top_n}
                onChange={(e) => setRetrievalSettings({ ...retrievalSettings, top_n: parseInt(e.target.value) || 3 })}
              />
              <p className="text-xs text-muted-foreground">
                Number of relevant chunks to retrieve (1-10)
              </p>
            </div>

            {/* Show Source Quotes */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="show_quote">Show Source Quotes</Label>
                <p className="text-xs text-muted-foreground">
                  Include source references in responses
                </p>
              </div>
              <Switch
                id="show_quote"
                checked={retrievalSettings.show_quote}
                onCheckedChange={(checked) => setRetrievalSettings({ ...retrievalSettings, show_quote: checked })}
              />
            </div>

            {/* Bottom buttons */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleResetRetrieval}>
                Reset to Defaults
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges || updateAssistant.isPending}>
                {updateAssistant.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save All Changes'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Web Crawler Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Web Crawler Settings
            <span className="ml-auto text-sm font-normal text-muted-foreground">
              {crawlerSettings.enable_crawler ? 'Enabled' : 'Disabled'}
            </span>
          </CardTitle>
          <CardDescription>Configure real-time web crawling during query retrieval</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Enable Crawler Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="enable_crawler">Enable Web Crawler</Label>
                <p className="text-xs text-muted-foreground">
                  Crawl websites in real-time during query retrieval
                </p>
              </div>
              <Switch
                id="enable_crawler"
                checked={crawlerSettings.enable_crawler}
                onCheckedChange={(checked) => setCrawlerSettings({ ...crawlerSettings, enable_crawler: checked })}
              />
            </div>

            {crawlerSettings.enable_crawler && (
              <>
                <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription className="text-blue-900 dark:text-blue-100 text-sm">
                    The crawler will search specified domains in real-time when answering queries.
                    This supplements your knowledge base with live web content.
                  </AlertDescription>
                </Alert>

                {/* Include Domains */}
                <div className="space-y-2">
                  <Label htmlFor="crawler_include_domains">
                    Include Domains
                  </Label>
                  <Textarea
                    id="crawler_include_domains"
                    rows={3}
                    value={crawlerSettings.crawler_include_domains}
                    onChange={(e) => setCrawlerSettings({ ...crawlerSettings, crawler_include_domains: e.target.value })}
                    placeholder="example.com, docs.example.com, help.example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated list of domains to crawl. Leave empty to allow all domains.
                  </p>
                </div>

                {/* Exclude Domains */}
                <div className="space-y-2">
                  <Label htmlFor="crawler_exclude_domains">
                    Exclude Domains (Optional)
                  </Label>
                  <Textarea
                    id="crawler_exclude_domains"
                    rows={2}
                    value={crawlerSettings.crawler_exclude_domains}
                    onChange={(e) => setCrawlerSettings({ ...crawlerSettings, crawler_exclude_domains: e.target.value })}
                    placeholder="admin.example.com, private.example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Domains to explicitly exclude from crawling
                  </p>
                </div>
              </>
            )}

            {/* Bottom buttons */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleResetCrawler}>
                Reset to Defaults
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges || updateAssistant.isPending}>
                {updateAssistant.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save All Changes'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Delete Assistant</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete assistant permanently?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete "{assistant.name}" 
                  and remove all associated configurations.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteAssistant.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Assistant'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  )
}
